/**
 * Customizable ball skins. Pure data + small localStorage helpers (no Phaser),
 * so both the Phaser Player and the React skin-picker can share them.
 *
 * `body`/`rim`/`highlight` paint the ball; `sclera`/`pupil`/`mouth` paint the
 * face. Add an entry here and it shows up everywhere automatically.
 */
export interface Skin {
  id: string;
  name: string;
  body: number;
  rim: number;
  highlight: number;
  sclera: number;
  pupil: number;
  mouth: number;
}

// Shared face palette (kept consistent across skins for readability).
const SCLERA = 0xffffff;
const PUPIL = 0x14161f;
const MOUTH = 0x10121a;
const HI = 0xffffff;

export const SKINS: readonly Skin[] = [
  // The CLASSIC legacy ball (legacy `player.png`): a warm red sphere with white
  // eyes and a dark open "uh-oh" mouth — the original Avoid! look, now the default.
  { id: 'classic', name: 'Classic', body: 0xe8423d, rim: 0xab2d2a, highlight: HI, sclera: SCLERA, pupil: PUPIL, mouth: MOUTH },
  { id: 'aqua', name: 'Aqua', body: 0x4cc9f0, rim: 0x2b8fb8, highlight: HI, sclera: SCLERA, pupil: PUPIL, mouth: MOUTH },
  { id: 'ember', name: 'Ember', body: 0xef476f, rim: 0xb22b4c, highlight: HI, sclera: SCLERA, pupil: PUPIL, mouth: MOUTH },
  { id: 'lime', name: 'Lime', body: 0x90be6d, rim: 0x5f8a45, highlight: HI, sclera: SCLERA, pupil: PUPIL, mouth: MOUTH },
  { id: 'grape', name: 'Grape', body: 0xb388ff, rim: 0x7a52c4, highlight: HI, sclera: SCLERA, pupil: PUPIL, mouth: MOUTH },
  { id: 'gold', name: 'Gold', body: 0xffd166, rim: 0xc99b2e, highlight: HI, sclera: SCLERA, pupil: PUPIL, mouth: MOUTH },
  { id: 'mono', name: 'Slate', body: 0x39405a, rim: 0x222740, highlight: HI, sclera: SCLERA, pupil: PUPIL, mouth: MOUTH },
];

export const DEFAULT_SKIN_ID = 'classic';

const SKIN_KEY = 'barrage.skin';

export function getSkin(id: string): Skin {
  return SKINS.find((s) => s.id === id) ?? SKINS[0];
}

/** Read the persisted skin id (validated against the catalog). */
export function loadSkinId(): string {
  try {
    const id = localStorage.getItem(SKIN_KEY);
    if (id && SKINS.some((s) => s.id === id)) return id;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_SKIN_ID;
}

export function saveSkinId(id: string): void {
  try {
    localStorage.setItem(SKIN_KEY, id);
  } catch {
    /* ignore */
  }
}

/** `0x4cc9f0` → `#4cc9f0`, for SVG/CSS. */
export function toHex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}
